// Service for managing region translations
import regionsTranslations from '../data/regions.json';
import api from './api';

export const addRegionTranslation = async (regionName, translations) => {
  try {
    // Send only French name to backend API
    const response = await api.post('/organization/regions', {
      name: translations.fr, // Send French name to backend
      code: regionName // Use region name as code for now, will be updated
    });

    if (response.data.success) {
      // Save all translations to localStorage for file update
      try {
        const existingTranslations = JSON.parse(localStorage.getItem('regionTranslations') || '{}');
        const updatedTranslations = {
          ...existingTranslations,
          [regionName]: translations
        };
        
        localStorage.setItem('regionTranslations', JSON.stringify(updatedTranslations));
        
        // Generate the complete JSON for regions.json
        const completeJson = {
          regions: {
            ...regionsTranslations.regions,
            ...updatedTranslations
          }
        };
        
        console.log('Region added to backend successfully!');
        console.log('To update src/data/regions.json, copy this JSON:');
        console.log(JSON.stringify(completeJson, null, 2));
        
        return {
          success: true,
          message: 'Region added successfully! Check console for updated regions.json content.',
          data: updatedTranslations
        };
      } catch (localError) {
        console.error('Error saving translations to localStorage:', localError);
        return {
          success: true,
          message: 'Region added to backend, but translation save failed',
          data: null
        };
      }
    } else {
      return {
        success: false,
        message: response.data.message || 'Error adding region'
      };
    }
  } catch (error) {
    console.error('Error adding region:', error);
    return {
      success: false,
      message: 'Error adding region to backend',
      error: error.message
    };
  }
};

export const getRegionTranslation = (regionName, language = 'fr') => {
  try {
    // First check localStorage for new translations (temporary storage)
    const localTranslations = JSON.parse(localStorage.getItem('regionTranslations') || '{}');
    if (localTranslations[regionName]) {
      return localTranslations[regionName][language] || regionName;
    }
    
    // Then check static translations from regions.json
    const translations = regionsTranslations.regions[regionName];
    if (!translations) {
      return regionName; // Return original name if no translation found
    }
    
    return translations[language] || regionName;
  } catch (error) {
    console.error('Error getting translation:', error);
    return regionName;
  }
};

export const getAllTranslations = () => {
  return regionsTranslations;
};

// Function to reload translations from localStorage
export const reloadTranslations = () => {
  try {
    const localTranslations = JSON.parse(localStorage.getItem('regionTranslations') || '{}');
    const combinedTranslations = {
      ...regionsTranslations,
      regions: {
        ...regionsTranslations.regions,
        ...localTranslations
      }
    };
    console.log('Translations reloaded from localStorage:', combinedTranslations);
    return combinedTranslations;
  } catch (error) {
    console.error('Error reloading translations:', error);
    return regionsTranslations;
  }
};

// Function to validate localStorage structure
export const validateTranslationsFile = () => {
  try {
    const localTranslations = JSON.parse(localStorage.getItem('regionTranslations') || '{}');
    
    // Check if structure is correct
    if (typeof localTranslations !== 'object') {
      console.error('Invalid localStorage structure: not an object');
      return false;
    }
    
    console.log('localStorage structure is valid');
    return true;
  } catch (error) {
    console.error('Error validating localStorage:', error);
    return false;
  }
};

// Function to get combined translations (static + localStorage)
export const getCombinedTranslations = () => {
  try {
    const localTranslations = JSON.parse(localStorage.getItem('regionTranslations') || '{}');
    return {
      regions: {
        ...regionsTranslations.regions,
        ...localTranslations
      }
    };
  } catch (error) {
    console.error('Error getting combined translations:', error);
    return regionsTranslations;
  }
};

// Function to get localStorage translations only
export const getLocalStorageTranslations = () => {
  try {
    return JSON.parse(localStorage.getItem('regionTranslations') || '{}');
  } catch (error) {
    console.error('Error getting localStorage translations:', error);
    return {};
  }
};

// Function to generate updated regions.json content
export const generateUpdatedRegionsJson = () => {
  try {
    const localTranslations = JSON.parse(localStorage.getItem('regionTranslations') || '{}');
    const updatedRegions = {
      regions: {
        ...regionsTranslations.regions,
        ...localTranslations
      }
    };
    
    return JSON.stringify(updatedRegions, null, 2);
  } catch (error) {
    console.error('Error generating updated regions.json:', error);
    return JSON.stringify(regionsTranslations, null, 2);
  }
};

// Function to save translations to file (shows instructions)
export const saveTranslationsToFile = () => {
  try {
    const updatedJson = generateUpdatedRegionsJson();
    
    // Create downloadable file
    const dataBlob = new Blob([updatedJson], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = 'regions.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('Updated regions.json content:');
    console.log(updatedJson);
    console.log('Copy this content to src/data/regions.json');
    
    return {
      success: true,
      message: 'Updated regions.json downloaded. Copy content to src/data/regions.json',
      content: updatedJson
    };
  } catch (error) {
    console.error('Error saving translations to file:', error);
    return {
      success: false,
      message: 'Error generating updated regions.json',
      error: error.message
    };
  }
};
